import { computed, effect, type ReadonlySignal } from '@preact/signals-core';

import {
  DndContext,
  type DndContextConfig,
  draggableDataName,
  droppableDataName,
} from '../dnd-context.js';
import type { Disabled, SortingStrategy, UniqueIdentifier } from '../types.js';
import { createDataDirective } from '../utils/data-directive.js';
import { asHTMLElement } from '../utils/element.js';

export type CommonSortContextConfig = {};
export type SortContextConfig = {
  strategy: SortingStrategy;
  items: ReadonlySignal<UniqueIdentifier[]>;
  id?: string;
  disabled?: ReadonlySignal<boolean | Disabled>;
  resolveNode?: (
    id: UniqueIdentifier,
    nodes: readonly HTMLElement[],
    activeNode: HTMLElement
  ) => HTMLElement | undefined;
} & DndContextConfig;

export class SortContext extends DndContext {
  disabled = computed(() => {
    const disabled = this.config.disabled?.value ?? false;
    return typeof disabled === 'boolean'
      ? { draggable: disabled, droppable: disabled }
      : {
          draggable: disabled.draggable ?? false,
          droppable: disabled.droppable ?? false,
        };
  });

  dragSourceList$ = computed(() => {
    const active = this.active$.value;
    if (!active) {
      return;
    }
    return this.items.value.flatMap(id => {
      const nodes = Array.from(
        this.container.querySelectorAll(
          `[${draggableDataName.attribute}='${id}']`
        )
      ).flatMap(node => {
        const element = asHTMLElement(node);
        return element ? [element] : [];
      });
      const ele = this.resolveNode(id, nodes, active.node);
      if (!ele) {
        return [];
      }
      return {
        node: ele,
        transformNodes: nodes,
        id,
        rect: ele.getBoundingClientRect(),
      };
    });
  });

  private readonly transformedNodes = new Set<HTMLElement>();

  private readonly nodeTransitions = new Map<HTMLElement, string>();

  private clearTransforms() {
    this.transformedNodes.forEach(node => {
      node.style.transform = '';
    });
    this.transformedNodes.clear();
  }

  private clearTransitions() {
    this.nodeTransitions.forEach((transition, node) => {
      node.style.transition = transition;
    });
    this.nodeTransitions.clear();
  }

  private ensureTransition(node: HTMLElement) {
    if (this.nodeTransitions.has(node)) return;
    this.nodeTransitions.set(node, node.style.transition);
    node.style.transition = 'transform 0.2s';
  }

  private resolveNode(
    id: UniqueIdentifier,
    nodes: readonly HTMLElement[],
    activeNode: HTMLElement
  ) {
    return (
      this.config.resolveNode?.(id, nodes, activeNode) ??
      nodes.find(node => node === activeNode) ??
      nodes[0]
    );
  }

  protected override resolveDroppableNode(
    id: UniqueIdentifier,
    nodes: readonly HTMLElement[],
    activeNode: HTMLElement
  ): HTMLElement | undefined {
    return this.resolveNode(id, nodes, activeNode);
  }

  get items() {
    return this.config.items;
  }

  get strategy() {
    return this.config.strategy;
  }

  constructor(override config: SortContextConfig) {
    super(config);
    effect(() => {
      const list = this.dragSourceList$.value;
      this.clearTransforms();
      if (list) {
        const transforms = this.strategy({
          rects: list.map(v => v.rect),
          activeNodeRect: this.collisionRect$.value,
          activeIndex: list.findIndex(v => v.id === this.active$.value?.id),
          overIndex: list.findIndex(v => v.id === this.overId$.value),
        });
        transforms.forEach((transform, i) => {
          const nodes = list[i]?.transformNodes;
          if (!nodes) {
            return;
          }
          nodes.forEach(node => {
            this.ensureTransition(node);
            if (transform != null) {
              node.style.transform = `translate3d(${Math.round(transform.x)}px,${Math.round(transform.y)}px,0)
    scaleX(${transform.scaleX}) scaleY(${transform.scaleY})`;
              this.transformedNodes.add(node);
            }
          });
        });
      } else {
        this.clearTransitions();
      }
    });
    this.disposables.push(() => {
      this.clearTransforms();
      this.clearTransitions();
    });
  }

  protected override isDragDisabled() {
    return this.disabled.value.draggable;
  }

  protected override acceptsIdentifier(id: UniqueIdentifier) {
    return this.items.value.includes(id);
  }
}

export const createSortContext = (config: SortContextConfig) => {
  return new SortContext(config);
};
const _sortable = createDataDirective(draggableDataName, droppableDataName);
export const sortable = (id: string) => {
  return _sortable(id, id);
};
